import { format } from 'date-fns';

/**
 * Mise en page commune aux fiches Partenaire (Extras) et Fournisseur, calquée sur le modèle fourni par
 * Emeni (2026-08-24, docs/maquettes/Fiche_Partenaire_Agence_Prestige_Evenements-v4.pdf) : badge de type en
 * capitales espacées, nom en très gros, logo à droite, deux cartes côte à côte (coordonnées / référent),
 * puis des cartes pleine largeur pour le catalogue et les notes, et un pied de page en deux temps.
 *
 * Les deux générateurs étaient jusqu'ici deux copies quasi identiques l'une de l'autre : le modèle vit
 * désormais ici une seule fois, chaque fiche ne décrivant plus que ce qui lui est propre (types d'extras
 * pour le partenaire, articles tarifés pour le fournisseur).
 *
 * Le bleu du modèle est remplacé partout par la couleur de l'établissement, et l'encadré « emplacement
 * logo » par son vrai logo dès qu'il en a un — demande explicite d'Emeni.
 */

export interface SheetContact {
    /** Nom deja compose, quand la source ne separe pas prenom et nom (ex: le referent d'une privatisation,
     *  saisi en un seul champ « organisateur »). Prioritaire sur firstName/lastName. */
    name?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
}

export interface SheetSection {
    /** Titre de la carte, rendu en capitales à côté de la puce colorée. */
    title: string;
    /** Lignes simples (catalogue de types) OU lignes à deux colonnes (article + prix). */
    rows: { left: string; right?: string }[];
    /** Message affiché à la place des lignes quand il n'y en a aucune. */
    emptyLabel?: string;
    /** Rend les lignes comme des pastilles côte à côte plutôt qu'en liste (types d'extras du modèle). */
    asChips?: boolean;
}

export interface EntitySheetData {
    id: string;
    /** Badge d'en-tête : « FICHE PARTENAIRE », « FICHE FOURNISSEUR »… */
    kindLabel: string;
    name: string;
    email?: string;
    phone?: string;
    address?: string;
    notes?: string;
    contact?: SheetContact;
    sections?: SheetSection[];
    logoUrl?: string | null;
    issuerName?: string;
    brandColor?: string;
    /** Titre de la carte de gauche. « Infos de l'entité » par défaut — un salarié, lui, a des « Coordonnées ». */
    infoTitle?: string;
    /**
     * Retire la carte « Référent contact », celle de gauche prenant toute la largeur. Un fournisseur ou
     * un contact professionnel a un référent ; un salarié n'en a pas, et une carte « Aucun référent
     * renseigné » sur sa fiche ne fait que poser une question sans objet.
     */
    withoutContactCard?: boolean;
}

export const hexToRgb = (hex?: string): [number, number, number] => {
    const clean = (hex || '#987b55').replace('#', '');
    const r = parseInt(clean.substring(0, 2), 16) || 0;
    const g = parseInt(clean.substring(2, 4), 16) || 0;
    const b = parseInt(clean.substring(4, 6), 16) || 0;
    return [r, g, b];
};

export const detectImageFormat = (url: string): 'PNG' | 'JPEG' | 'WEBP' => {
    if (!url) return 'PNG';
    const lower = url.toLowerCase();
    if (lower.includes('image/png') || lower.endsWith('.png')) return 'PNG';
    if (lower.includes('image/webp') || lower.endsWith('.webp')) return 'WEBP';
    if (lower.includes('image/jpg') || lower.includes('image/jpeg') || lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'JPEG';
    return 'PNG';
};

// Repères de la page A4, tous en millimètres — le modèle respire beaucoup, d'où des marges larges.
const M = 15;              // marge gauche/droite
const W = 210 - M * 2;     // largeur utile
const COL_GAP = 6;
const COL_W = (W - COL_GAP) / 2;

// Deux colonnes pour les lignes question / reponse. La reponse avait jusqu'ici toute la largeur qu'elle
// voulait et debordait de la carte ; la question, elle, etait bornee mais amputee de ses lignes suivantes.
const LARGEUR_REPONSE = 52;
const LARGEUR_QUESTION = W - 10 - LARGEUR_REPONSE - 5;

/**
 * Les polices standard de jsPDF n'encodent que le jeu WinAnsi : une fleche « -> » saisie en Unicode y
 * ressort en « !' », ce qu'Emeni a vu sur ses horaires d'arrivee et de depart (2026-08-28). Plutot que
 * d'embarquer une police complete pour quelques signes, on les remplace par leur equivalent lisible, et on
 * ecarte ce qui resterait hors du jeu pour ne jamais afficher de charabia a la place d'un texte.
 */
const assainirPdf = (texte: string): string => String(texte || '')
    .replace(/[→➡➔]/g, '->')
    .replace(/[⇒]/g, '=>')
    .replace(/[←]/g, '<-')
    .replace(/[✓✔]/g, 'v')
    .replace(/[✗✘×]/g, 'x')
    .replace(/[≤]/g, '<=')
    .replace(/[≥]/g, '>=')
    .replace(/[   ]/g, ' ')
    // La ponctuation typographique est hors Latin-1 alors qu'elle abonde dans du texte saisi : une
    // apostrophe courbe dans « l'evenement » serait devenue un blanc au milieu du mot.
    .replace(/[‘’‛]/g, "'")
    .replace(/[“”„]/g, '"')
    .replace(/[–—―]/g, '-')
    .replace(/[•●▪]/g, '-')
    .replace(/[…]/g, '...')
    // Tout ce qui sort de Latin-1 apres ces substitutions serait dessine de travers : mieux vaut un espace
    // qu'un caractere faux, la phrase reste lisible.
    .replace(/[^ -ÿ]/g, ' ');

const INK = [15, 23, 42] as const;        // texte principal (presque noir)
const MUTED = [100, 116, 139] as const;   // libellés en petites capitales
const FAINT = [148, 163, 184] as const;   // pied de page, texte absent
const LINE = [226, 232, 240] as const;    // bordures des cartes

export const buildEntitySheet = async (data: EntitySheetData) => {
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const brand = hexToRgb(data.brandColor || '#2563eb');

    /** Libellé en petites capitales grises, tel que le modèle titre chaque valeur. */
    const label = (text: string, x: number, y: number) => {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(6.5);
        doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
        doc.text(text.toUpperCase(), x, y, { charSpace: 0.3 });
    };

    /** Valeur sous son libellé. Coupée à la largeur de la carte pour ne jamais déborder. */
    const value = (text: string, x: number, y: number, maxW: number, bold = false) => {
        doc.setFont('helvetica', bold ? 'bold' : 'normal');
        doc.setFontSize(9);
        doc.setTextColor(INK[0], INK[1], INK[2]);
        const lines = doc.splitTextToSize(text, maxW);
        doc.text(lines, x, y);
        return lines.length;
    };

    /** Titre de section : petite puce carrée à la couleur de l'établissement + intitulé en capitales. */
    const sectionTitle = (text: string, x: number, y: number) => {
        doc.setFillColor(brand[0], brand[1], brand[2]);
        doc.rect(x, y - 2.2, 2.2, 2.2, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.5);
        doc.setTextColor(INK[0], INK[1], INK[2]);
        doc.text(text.toUpperCase(), x + 4.5, y, { charSpace: 0.4 });
    };

    const card = (x: number, y: number, w: number, h: number) => {
        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(LINE[0], LINE[1], LINE[2]);
        doc.setLineWidth(0.3);
        doc.roundedRect(x, y, w, h, 2.5, 2.5, 'FD');
    };

    // ---------- En-tête : badge, nom, logo ----------
    let y = 20;
    const badgeW = doc.getTextWidth(data.kindLabel.toUpperCase()) * 0.62 + 8;
    doc.setFillColor(brand[0], brand[1], brand[2]);
    doc.roundedRect(M, y - 4, badgeW, 5.5, 1.2, 1.2, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.5);
    doc.setTextColor(255, 255, 255);
    doc.text(data.kindLabel.toUpperCase(), M + 4, y - 0.3, { charSpace: 0.5 });

    y += 9;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(19);
    doc.setTextColor(INK[0], INK[1], INK[2]);
    // Le nom peut être long : on le coupe sur la largeur restante, le logo occupant la droite.
    const nameLines = doc.splitTextToSize(data.name, W - 42);
    doc.text(nameLines, M, y);

    if (data.logoUrl) {
        const maxW = 30, maxH = 18;
        let dw = maxW, dh = maxH;
        try {
            const props = doc.getImageProperties(data.logoUrl);
            const ratio = props.width / props.height;
            if (ratio > maxW / maxH) { dw = maxW; dh = maxW / ratio; } else { dh = maxH; dw = maxH * ratio; }
        } catch (e) { /* repli sur la boîte par défaut */ }
        try {
            doc.addImage(data.logoUrl, detectImageFormat(data.logoUrl), 210 - M - dw, 14, dw, dh);
        } catch (e) {
            try { doc.addImage(data.logoUrl, 210 - M - dw, 14, dw, dh); } catch (e2) { /* logo illisible : on n'affiche rien */ }
        }
    }

    y += (nameLines.length - 1) * 8 + 8;
    doc.setDrawColor(LINE[0], LINE[1], LINE[2]);
    doc.setLineWidth(0.3);
    doc.line(M, y, 210 - M, y);
    y += 9;

    // ---------- Deux cartes : coordonnées / référent ----------
    const infoRows: [string, string][] = [];
    if (data.email) infoRows.push(['Email', data.email]);
    if (data.phone) infoRows.push(['Téléphone', data.phone]);
    if (data.address) infoRows.push(['Adresse', data.address]);

    const contactName = (data.contact?.name || '').trim() || `${data.contact?.firstName || ''} ${data.contact?.lastName || ''}`.trim();
    const contactRows: [string, string, boolean][] = [];
    if (contactName) contactRows.push(['Nom du contact', contactName, true]);
    if (data.contact?.email) contactRows.push(['Email direct', data.contact.email, false]);
    if (data.contact?.phone) contactRows.push(['Téléphone direct', data.contact.phone, false]);

    // Hauteur commune aux deux cartes : elles restent alignées même si l'une a moins de lignes.
    const rowsH = (rows: unknown[]) => Math.max(1, rows.length) * 11;
    const sansReferent = !!data.withoutContactCard;
    const cardH = 12 + (sansReferent ? rowsH(infoRows) : Math.max(rowsH(infoRows), rowsH(contactRows)));
    const largeurInfo = sansReferent ? COL_W * 2 + COL_GAP : COL_W;
    card(M, y, largeurInfo, cardH);
    if (!sansReferent) card(M + COL_W + COL_GAP, y, COL_W, cardH);

    sectionTitle(data.infoTitle || "Infos de l'entité", M + 5, y + 7);
    if (!sansReferent) sectionTitle('Référent contact', M + COL_W + COL_GAP + 5, y + 7);

    let ly = y + 15;
    if (infoRows.length === 0) {
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(8);
        doc.setTextColor(FAINT[0], FAINT[1], FAINT[2]);
        doc.text('Aucune coordonnée renseignée.', M + 5, ly);
    } else {
        infoRows.forEach(([l, v]) => {
            label(l, M + 5, ly);
            const n = value(v, M + 5, ly + 4.5, largeurInfo - 10);
            ly += 11 + (n - 1) * 4;
        });
    }

    let ry = y + 15;
    if (sansReferent) {
        // Rien à droite : la carte de gauche occupe déjà toute la largeur.
    } else if (contactRows.length === 0) {
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(8);
        doc.setTextColor(FAINT[0], FAINT[1], FAINT[2]);
        doc.text('Aucun référent renseigné.', M + COL_W + COL_GAP + 5, ry);
    } else {
        contactRows.forEach(([l, v, bold]) => {
            label(l, M + COL_W + COL_GAP + 5, ry);
            const n = value(v, M + COL_W + COL_GAP + 5, ry + 4.5, COL_W - 10, bold);
            ry += 11 + (n - 1) * 4;
        });
    }

    y += cardH + 8;

    // ---------- Sections pleine largeur (catalogue) ----------
    for (const section of data.sections || []) {
        const rows = section.rows || [];
        let bodyH: number;
        // La question et la reponse sont decoupees AVANT de calculer la hauteur : une question longue
        // occupe deux ou trois lignes, et la carte doit les contenir. Jusqu'ici on comptait 6,5 mm par
        // ligne quoi qu'il arrive, et le dessin ne gardait que la premiere ligne de la question — les
        // questions longues du questionnaire etaient donc tronquees net, et les reponses debordaient de
        // la carte faute d'etre decoupees du tout. Signale par Emeni (2026-08-28).
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        const mesures = section.asChips ? [] : rows.map((r) => {
            const g = doc.splitTextToSize(assainirPdf(r.left || ''), LARGEUR_QUESTION);
            const d = r.right ? doc.splitTextToSize(assainirPdf(r.right), LARGEUR_REPONSE) : [];
            return { g, d, lignes: Math.max(g.length, d.length || 1) };
        });
        if (section.asChips) {
            bodyH = rows.length ? Math.ceil(rows.length / 4) * 8 + 2 : 8;
        } else {
            bodyH = Math.max(1, mesures.reduce((t, m) => t + m.lignes * 4.6 + 1.9, 0)) + 2;
        }
        const h = 14 + bodyH;
        if (y + h > 268) { doc.addPage(); y = 20; }
        card(M, y, W, h);
        sectionTitle(section.title, M + 5, y + 7);

        let sy = y + 15;
        if (rows.length === 0) {
            doc.setFont('helvetica', 'italic');
            doc.setFontSize(8);
            doc.setTextColor(FAINT[0], FAINT[1], FAINT[2]);
            doc.text(section.emptyLabel || 'Rien à afficher.', M + 5, sy);
        } else if (section.asChips) {
            // Pastilles à fond léger, comme « Accueil » dans le modèle.
            let cx = M + 5;
            rows.forEach((r) => {
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(7.5);
                const w = doc.getTextWidth(r.left) + 7;
                if (cx + w > 210 - M - 5) { cx = M + 5; sy += 8; }
                doc.setFillColor(brand[0], brand[1], brand[2]);
                doc.roundedRect(cx, sy - 3.6, w, 5.6, 1.4, 1.4, 'F');
                doc.setTextColor(255, 255, 255);
                doc.text(r.left, cx + 3.5, sy);
                cx += w + 3;
            });
        } else {
            rows.forEach((r, i) => {
                const m = mesures[i];
                if (sy + m.lignes * 4.6 > 272) { doc.addPage(); sy = 20; }
                doc.setTextColor(INK[0], INK[1], INK[2]);
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(8.5);
                m.g.forEach((ligne: string, k: number) => doc.text(ligne, M + 5, sy + k * 4.6));
                if (m.d.length) {
                    doc.setFont('helvetica', 'bold');
                    m.d.forEach((ligne: string, k: number) => doc.text(ligne, 210 - M - 5, sy + k * 4.6, { align: 'right' }));
                }
                sy += m.lignes * 4.6 + 1.9;
            });
        }
        y += h + 6;
    }

    // ---------- Notes, avec le filet vertical coloré du modèle ----------
    if (data.notes) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        const noteLines = doc.splitTextToSize(data.notes, W - 18);
        const h = 14 + noteLines.length * 4.6 + 4;
        if (y + h > 268) { doc.addPage(); y = 20; }
        card(M, y, W, h);
        sectionTitle('Notes & remarques', M + 5, y + 7);
        doc.setFillColor(brand[0], brand[1], brand[2]);
        doc.rect(M + 5, y + 11, 1.2, noteLines.length * 4.6 + 1, 'F');
        doc.setTextColor(INK[0], INK[1], INK[2]);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        doc.text(noteLines, M + 10, y + 15);
        y += h + 6;
    }

    // ---------- Pied de page : émetteur à gauche, référence et date à droite ----------
    const pages = doc.getNumberOfPages();
    doc.setPage(pages);
    doc.setDrawColor(LINE[0], LINE[1], LINE[2]);
    doc.setLineWidth(0.3);
    doc.line(M, 281, 210 - M, 281);
    if (data.issuerName) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor(FAINT[0], FAINT[1], FAINT[2]);
        doc.text('Fiche établie par ', M, 286);
        const w = doc.getTextWidth('Fiche établie par ');
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(INK[0], INK[1], INK[2]);
        doc.text(data.issuerName, M + w, 286);
    }
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(FAINT[0], FAINT[1], FAINT[2]);
    doc.text(`Réf. ${data.id} • Généré le ${format(new Date(), 'dd/MM/yyyy à HH:mm')} • iAmani Connexion`, 210 - M, 286, { align: 'right' });

    return doc;
};
